import { useEffect, useState } from "react";
import { CheckCircle2, Copy, Link2, Loader2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Disclaimer } from "@/components/Disclaimer";

const url = (t) => `${window.location.origin}/share/${t}`;

export const SharePanel = ({ project, onChange }) => {
  const [auto, setAuto] = useState(true);
  const [busy, setBusy] = useState(false);
  const share = project.share;
  const [price, setPrice] = useState(null);
  useEffect(() => {
    const sqft = project.unlock_sqft || 0;
    if (!project.locked || sqft < 1) return;
    api.get("/billing/quote", { params: { sqft } }).then(({ data }) => setPrice(data.total)).catch(() => {});
  }, [project.locked, project.unlock_sqft]);
  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); await onChange(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const create = () => run(() => api.post(`/projects/${project.project_id}/share`, { auto_unlock: auto }), "Client link created");
  const ap = share?.approval;
  return (
    <div className="space-y-3" data-testid="share-panel">
      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--cyan)] flex items-center gap-1.5"><Link2 size={12} /> Client approval link</p>
      {!share ? (
        <>
          <p className="text-xs text-[var(--muted)]">Send clients a page with this layout (no dimensions) where they can approve it.</p>
          {project.locked && (
            <label className="flex items-start gap-2 text-xs text-[var(--muted)]">
              <input data-testid="share-auto-unlock-checkbox" type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)} className="mt-0.5 accent-[var(--amber)]" />
              <span>When the client approves, unlock this draft by charging my card on file (<span data-testid="share-unlock-price">${(price ?? project.unlock_sqft ?? 0).toLocaleString()}</span>)</span>
            </label>
          )}
          <button data-testid="create-share-btn" onClick={create} disabled={busy} className="w-full py-2.5 text-sm border border-[var(--line)] hover:border-[var(--cyan)] transition-colors flex items-center justify-center gap-2 disabled:opacity-50">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Link2 size={14} />} Create client link
          </button>
        </>
      ) : (
        <>
          <div className="flex gap-2">
            <input readOnly value={url(share.token)} data-testid="share-link-input" className="flex-1 min-w-0 bg-[var(--card)] border border-[var(--line)] px-3 py-2 text-xs font-mono" />
            <button data-testid="copy-share-btn" onClick={async () => { await navigator.clipboard.writeText(url(share.token)); toast.success("Link copied"); }} className="px-3 border border-[var(--line)] hover:border-[var(--cyan)]"><Copy size={14} /></button>
          </div>
          <p className="text-xs flex items-center gap-1.5" data-testid="share-status">
            {ap ? <><CheckCircle2 size={13} className="text-[var(--emerald)]" /> Approved by {ap.name} ({ap.email}){ap.unlocked ? " · unlocked" : ""}</>
              : <span className="text-[var(--muted)]">Waiting for client approval{share.auto_unlock && project.locked ? " · auto-unlock on" : ""}</span>}
          </p>
          {ap?.note && <p className="text-xs text-[var(--muted)] border-l-2 border-[var(--line)] pl-2">"{ap.note}"</p>}
          <button data-testid="revoke-share-btn" onClick={() => run(() => api.delete(`/projects/${project.project_id}/share`), "Link turned off")} disabled={busy}
            className="text-xs text-[var(--muted)] hover:text-red-400 flex items-center gap-1"><XCircle size={12} /> Turn off link</button>
        </>
      )}
      <Disclaimer compact />
    </div>
  );
};
